import * as React from 'react';
import { FileCheck2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Task } from '@klareospace/pharos-lib-types';

import { useProofViewDialog } from '../hooks/use-proof-view-dialog';
import { flattenJobProofs } from '../utils/proof-labels';
import { ProofsTable } from './proofs-table';
import { ProofViewDialog } from './proof-view-dialog';
import { TaskDetailSection } from './task-detail-section';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TaskProofsSectionProps {
  readonly task: Task;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * "Proofs" section in `TaskDetailDialog`, rendered after the timeline and
 * carrier/driver sections. Reuses the same `ProofsTable` as `OrderProofsTab`,
 * but without a `header` — per the mockup, the table has no "Task X of Y"
 * label when shown inside a single task's detail dialog. `ProofsTable` still
 * renders its own rounded/bordered card here, identical to `OrderProofsTab`.
 */
export function TaskProofsSection({ task }: TaskProofsSectionProps): React.JSX.Element {
  const { t } = useTranslation('tasks');
  const { onOpenChange, openProofView, selectedProof } = useProofViewDialog();

  return (
    <TaskDetailSection icon={FileCheck2} title={t('proofs.title')}>
      <ProofsTable onView={openProofView} proofs={flattenJobProofs(task.jobs)} />
      <ProofViewDialog jobProof={selectedProof} onOpenChange={onOpenChange} />
    </TaskDetailSection>
  );
}
